<template>
  <div class="row">
    <BaseButtonDropdown
      class="row"
      placement="bottom-start"
    >
      <template #btn="{ toggle }">
        <Tag
          v-if="hasOneTag"
          class="one-tag"
          :color="firstTag.color"
          :hide-cursor="!clickable"
          :hide-hover="!clickable"
          hide-remove-button
          :icon="firstTag.icon"
          :label="firstTag.label"
          @click="onFirstTagClick"
        />

        <BaseButton
          v-else
          class="ellipsis"
          :class="[
            { 'text-ds-denim-9 border-ds-denim-9': hasTags },
            { 'text-ds-denim-3 border-ds-denim-3': !hasTags },
          ]"
          data-testid="tags-button"
          dense
          :disabled="!hasTags"
          flat
          :label="tagLabel"
          new-design
          @click="toggle"
        />
      </template>

      <BaseInputTags
        v-if="hasTags"
        choose-color
        class="tags-container"
        clearable
        close-on-select
        hide-input
        hide-remove-button
        :hide-tag-cursor="!clickable"
        :model-value="tags"
        new-design
        option-label="label"
        option-prop="label"
        :options="tags"
        options-only
        tags-class="q-gutter-xs"
        @click="onInputTagClick"
      />
    </BaseButtonDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseButton from "shared/components/base/BaseButton";
import BaseButtonDropdown from "shared/components/base/BaseButtonDropdown";
import BaseInputTags, {
  type InputTagsValue,
} from "shared/components/base/BaseInputTags";
import type { Props as TagProps } from "shared/components/base/BaseTag";
import Tag from "shared/components/base/BaseTag";

const tags = defineModel<TagProps[]>({
  default: () => [],
});

const props = defineProps<{
  clickable?: boolean;
  lists?: boolean;
}>();

const emit = defineEmits<{
  click: [TagProps];
}>();

const tagLabel = computed(() => {
  const count = tags.value.length;
  const labelKey = props.lists ? "tags.list_label" : "tags.label";

  return getLocaleText(labelKey, { count });
});

const firstTag = computed(() => tags.value[0]);

const hasOneTag = computed(() => tags.value.length === 1);

const hasTags = computed(() => tags.value.length > 0);

function onFirstTagClick(): void {
  if (!props.clickable) return;

  emit("click", firstTag.value);
}

function onInputTagClick(tag: InputTagsValue): void {
  if (typeof tag === "string") return;

  emit("click", tag);
}
</script>

<style lang="scss" scoped>
:deep(.tag) {
  font-size: 13px;
  padding: 6px;
}

.one-tag {
  max-width: 178px;
  height: 26px;
}

.tags {
  padding: 4px 8px;
  border-radius: 3px;
  border: 1px solid;
  font-size: 13px;
  display: block;
  height: 100%;
}

.add-tag {
  padding: 0 5px;
  font-size: 13px;
  background-color: var(--s-color-denim-9);
  border-radius: 3px;

  &:hover {
    background-color: var(--s-color-denim-9);
    border: 1px solid var(--s-color-denim-9);
    border-radius: 3px;
  }
}

.tags-container {
  padding: 10px;
  background-color: white;
  border: 1px solid $hover-background;
  border-radius: 3px;
  box-shadow: 0 2px 7px rgb(0 0 0 / 15%);
}

.add-tag-container {
  min-width: 260px;
}
</style>
